class AgeVerification extends HTMLElement {
  constructor() {
    super();

    this.confirmButton = this.querySelector('[data-age-verification-confirm]');
    this.declineButton = this.querySelector('[data-age-verification-decline]');
    this.deniedMessage = this.querySelector('[data-age-verification-denied]');
    this.dayInput = this.querySelector('[data-age-verification-day]');
    this.monthInput = this.querySelector('[data-age-verification-month]');
    this.yearInput = this.querySelector('[data-age-verification-year]');
    this.dobError = this.querySelector('[data-age-verification-dob-error]');
    this.focusableElements = Array.from(this.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleConfirm = this.handleConfirm.bind(this);
  }

  connectedCallback() {
    if (this.isAccepted()) {
      this.close();
      return;
    }

    document.documentElement.classList.add('age-verification-lock');
    document.documentElement.classList.remove('age-verification-accepted');

    this.confirmButton?.addEventListener('click', this.handleConfirm);
    this.declineButton?.addEventListener('click', () => this.decline());
    document.addEventListener('keydown', this.handleKeydown);

    if (this.mode === 'dob') {
      [this.dayInput, this.monthInput, this.yearInput].forEach((input) => {
        if (!input) return;
        input.addEventListener('input', () => this.handleDobInput(input));
      });
    }

    requestAnimationFrame(() => {
      if (this.mode === 'dob' && this.dayInput) {
        this.dayInput.focus();
      } else {
        this.confirmButton?.focus();
      }
    });
  }

  handleConfirm() {
    if (this.mode !== 'dob') {
      this.accept();
      return;
    }

    const result = this.validateDob();
    if (result.valid) {
      this.hideDobError();
      this.accept();
    } else {
      this.showDobError(result.message);
      if (result.field) result.field.focus();
    }
  }

  handleDobInput(input) {
    if (input.value.length >= Number(input.getAttribute('maxlength') || (input === this.yearInput ? 4 : 2))) {
      const order = [this.dayInput, this.monthInput, this.yearInput];
      const next = order[order.indexOf(input) + 1];
      if (next) next.focus();
    }
    if (this.dobError && !this.dobError.hidden) {
      this.hideDobError();
    }
  }

  validateDob() {
    const day = parseInt(this.dayInput?.value, 10);
    const month = parseInt(this.monthInput?.value, 10);
    const year = parseInt(this.yearInput?.value, 10);

    if (!day || !month || !year) {
      return { valid: false, message: this.invalidMessage, field: !day ? this.dayInput : !month ? this.monthInput : this.yearInput };
    }

    if (year < 1900 || year > new Date().getFullYear()) {
      return { valid: false, message: this.invalidMessage, field: this.yearInput };
    }

    const dob = new Date(year, month - 1, day);
    if (dob.getFullYear() !== year || dob.getMonth() !== month - 1 || dob.getDate() !== day) {
      return { valid: false, message: this.invalidMessage, field: this.dayInput };
    }

    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;

    if (age < this.minimumAge) {
      return { valid: false, message: this.underageMessage };
    }

    return { valid: true };
  }

  showDobError(message) {
    if (!this.dobError) return;
    this.dobError.textContent = message;
    this.dobError.hidden = false;
  }

  hideDobError() {
    if (!this.dobError) return;
    this.dobError.hidden = true;
    this.dobError.textContent = '';
  }

  get mode() {
    return this.dataset.mode || 'confirm';
  }

  get minimumAge() {
    return Number(this.dataset.minimumAge) || 18;
  }

  get underageMessage() {
    return this.dataset.underageMessage || 'Não tem idade legal para aceder a esta loja.';
  }

  get invalidMessage() {
    return this.dataset.invalidMessage || 'Por favor introduza uma data válida.';
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  isAccepted() {
    try {
      const stored = window.localStorage.getItem(this.storageKey);
      return Boolean(stored && JSON.parse(stored).expiresAt > Date.now());
    } catch (error) {
      return false;
    }
  }

  accept() {
    const rememberDays = Number(this.dataset.rememberDays) || 30;
    const expiresAt = Date.now() + rememberDays * 24 * 60 * 60 * 1000;

    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify({ expiresAt }));
    } catch (error) {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify({ expiresAt }));
    }

    this.close();
  }

  decline() {
    if (!this.deniedMessage) return;

    this.deniedMessage.hidden = false;
    this.declineButton?.setAttribute('aria-describedby', this.deniedMessage.id || 'AgeVerificationText');
  }

  close() {
    document.documentElement.classList.remove('age-verification-lock');
    document.documentElement.classList.add('age-verification-accepted');
    this.remove();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      return;
    }

    if (event.key !== 'Tab' || this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  get storageKey() {
    return this.dataset.storageKey || 'drshampoo.ageVerification';
  }
}

customElements.define('age-verification', AgeVerification);
